/**
 * Color Normalization Utilities
 *
 * Ensures that colors are stored consistently as hex values
 * with opacity stored separately, never as rgba values.
 */

/**
 * Extract hex color and opacity from an rgba string
 * @param rgba - rgba(r, g, b, a) string
 * @returns {hex: string, opacity: number} or null if invalid
 */
export function parseRgba(rgba: string): { hex: string; opacity: number } | null {
  const match = rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*([\d.]+)?\)/);
  if (!match) return null;

  const [, r, g, b, a] = match;
  const opacity = a ? parseFloat(a) * 100 : 100; // Convert 0-1 to 0-100

  // Convert RGB to hex
  const toHex = (n: string) => {
    const num = parseInt(n, 10);
    return num.toString(16).padStart(2, '0');
  };

  const hex = `#${toHex(r)}${toHex(g)}${toHex(b)}`;

  return { hex, opacity };
}

/**
 * Check if a string is an rgba value
 */
export function isRgba(value: string): boolean {
  return value.startsWith('rgba(') || value.startsWith('rgb(');
}

/**
 * Check if a string is a hex color
 */
export function isHex(value: string): boolean {
  return /^#[0-9A-Fa-f]{6}$/.test(value);
}

/**
 * Normalize a customization object to ensure all colors are hex
 * and opacity values are stored separately
 */
export function normalizeCustomization(customization: any): any {
  const normalized = { ...customization };

  // List of color fields that should be hex only (never rgba)
  const hexOnlyFields = [
    'primaryColor',
    'secondaryColor',
    'textColor',
    'accentColor',
    'cardBg',
    'cardGradientColor1',
    'cardGradientColor2',
    'dropdownColor',
  ];

  // Fields that can have opacity stored separately
  const fieldsWithOpacity: Array<{ color: string; opacity: string }> = [
    { color: 'navbarBg', opacity: 'navbarOpacity' },
    { color: 'tabBarBg', opacity: 'tabBarOpacity' },
  ];

  // Normalize hex-only fields
  for (const field of hexOnlyFields) {
    if (normalized[field] && isRgba(normalized[field])) {
      const parsed = parseRgba(normalized[field]);
      if (parsed) {
        console.warn(`⚠️ [Normalize] Converting ${field} from rgba to hex:`, normalized[field], '→', parsed.hex);
        normalized[field] = parsed.hex;
      }
    }
  }

  // Normalize fields with separate opacity
  for (const { color, opacity } of fieldsWithOpacity) {
    if (normalized[color] && isRgba(normalized[color])) {
      const parsed = parseRgba(normalized[color]);
      if (parsed) {
        console.warn(`⚠️ [Normalize] Converting ${color} from rgba to hex+opacity:`,
          normalized[color], '→', `${parsed.hex} + ${opacity}=${parsed.opacity}`);
        normalized[color] = parsed.hex;
        normalized[opacity] = parsed.opacity;
      }
    }
  }

  return normalized;
}
